import { useMemo } from "react";
import { Drawer, StatusTag } from "../../components/Primitives";
import { formatDateTime, formatTaskType } from "../../lib/format";
import { useApiQuery } from "../../lib/query";
import type {
  AccessibilitySnapshotArchive,
  SocialStatusArchive,
  Task,
  TaskResult,
} from "../../lib/types";
import { selectRelevantSnapshots } from "./task-detail-data";

const platformLabels: Record<string, string> = {
  wechat: "微信",
  enterprise_wechat: "企业微信",
  xiaohongshu: "小红书",
  douyin: "抖音",
};

export function TaskDetail({ task, onClose }: { task: Task; onClose(): void }) {
  const counts = task.results.reduce<Record<string, number>>((acc, result) => {
    acc[result.status] = (acc[result.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <Drawer
      title={formatTaskType(task.taskType)}
      subtitle={`任务 #${task.id} · ${formatDateTime(task.createdAt)}`}
      onClose={onClose}
      wide
    >
      <div className="task-detail-summary">
        <StatusTag value={task.status} />
        <span>{task.results.length} 台目标设备</span>
      </div>
      <dl className="detail-list">
        <div>
          <dt>任务编号</dt>
          <dd>{task.id}</dd>
        </div>
        <div>
          <dt>创建人账号</dt>
          <dd>{task.createdBy}</dd>
        </div>
        <div>
          <dt>目标类型</dt>
          <dd>{task.targetType}</dd>
        </div>
        <div>
          <dt>成功 / 失败</dt>
          <dd>
            {counts.succeeded || 0} /{" "}
            {(counts.failed || 0) + (counts.timeout || 0)}
          </dd>
        </div>
      </dl>
      <section className="drawer-section">
        <h3>设备执行结果</h3>
        <div className="task-result-cards">
          {task.results.map((result) => (
            <DeviceTaskResult key={result.id} result={result} />
          ))}
          {!task.results.length ? (
            <p className="inline-empty">尚无设备执行结果。</p>
          ) : null}
        </div>
      </section>
      <TaskArchiveDetails task={task} />
      <section className="drawer-section">
        <h3>任务参数</h3>
        <JsonPreview value={task.payload} />
      </section>
    </Drawer>
  );
}

function DeviceTaskResult({ result }: { result: TaskResult }) {
  return (
    <article className="task-result-card">
      <header>
        <div>
          <strong>设备 #{result.deviceId}</strong>
          <span>结果 #{result.id}</span>
        </div>
        <StatusTag value={result.status} />
      </header>
      <dl>
        <div>
          <dt>发送时间</dt>
          <dd>{formatDateTime(result.sentAt)}</dd>
        </div>
        <div>
          <dt>确认时间</dt>
          <dd>{formatDateTime(result.acknowledgedAt)}</dd>
        </div>
        <div>
          <dt>完成时间</dt>
          <dd>{formatDateTime(result.finishedAt)}</dd>
        </div>
        <div>
          <dt>网关</dt>
          <dd>{result.gatewayId || "—"}</dd>
        </div>
      </dl>
      {result.errorMessage ? (
        <p className="task-result-error">{result.errorMessage}</p>
      ) : null}
      <div className="task-result-payload">
        <h4>完整返回结果</h4>
        <JsonPreview value={result.result} empty="设备未返回结果内容。" />
      </div>
    </article>
  );
}

function TaskArchiveDetails({ task }: { task: Task }) {
  const showsSocialStatuses = task.taskType === "check_social_status";
  const showsSnapshots = task.taskType === "capture_accessibility_snapshot";
  const deviceIds = useMemo(
    () => new Set(task.results.map((result) => result.deviceId)),
    [task.results],
  );
  const socialQuery = useApiQuery<SocialStatusArchive[]>(
    ["archives", "social-statuses", "task-detail"],
    "/api/archives/social-statuses?limit=200",
    { enabled: showsSocialStatuses },
  );
  const snapshotQuery = useApiQuery<AccessibilitySnapshotArchive[]>(
    ["archives", "accessibility-snapshots", "task-detail"],
    "/api/archives/accessibility-snapshots?limit=200",
    { enabled: showsSnapshots },
  );
  const socialStatuses = useMemo(
    () =>
      (socialQuery.data || []).filter((item) => deviceIds.has(item.deviceId)),
    [deviceIds, socialQuery.data],
  );
  const snapshots = useMemo(
    () => selectRelevantSnapshots(task, snapshotQuery.data || [], deviceIds),
    [deviceIds, snapshotQuery.data, task],
  );

  if (!showsSocialStatuses && !showsSnapshots) {
    return null;
  }

  const query = showsSocialStatuses ? socialQuery : snapshotQuery;
  return (
    <section className="drawer-section">
      <h3>关联采集数据</h3>
      <p className="task-archive-note">
        展示目标设备最近归档的数据；上方“完整返回结果”是本次任务的精确执行结果。
      </p>
      {query.isPending ? (
        <p className="inline-empty">正在加载采集数据…</p>
      ) : null}
      {query.isError ? (
        <p className="task-result-error">
          采集数据加载失败，请确认账号具有归档读取权限。
        </p>
      ) : null}
      {query.isSuccess && showsSocialStatuses ? (
        <SocialStatusList items={socialStatuses} />
      ) : null}
      {query.isSuccess && showsSnapshots ? (
        <AccessibilitySnapshotList items={snapshots} />
      ) : null}
    </section>
  );
}

function SocialStatusList({ items }: { items: SocialStatusArchive[] }) {
  if (!items.length) {
    return <p className="inline-empty">未找到目标设备的社交应用状态归档。</p>;
  }
  return (
    <div className="archive-result-list">
      {items.map((item) => (
        <details key={item.id} className="archive-result-row">
          <summary>
            <span>
              <strong>{platformLabels[item.platform] || item.platform}</strong>
              <small>
                {item.packageName} · {formatDateTime(item.checkedAt)}
              </small>
            </span>
            <span className="archive-result-badges">
              <StatusTag
                value={item.installed ? "installed" : "missing"}
                tone={item.installed ? "green" : "red"}
              />
              <StatusTag value={item.enabled ? "enabled" : "disabled"} />
            </span>
          </summary>
          <dl>
            <div>
              <dt>登录状态</dt>
              <dd>
                {item.loginStatus === "unknown" ? "未知" : item.loginStatus}
              </dd>
            </div>
            <div>
              <dt>应用版本</dt>
              <dd>{item.versionName || item.versionCode || "—"}</dd>
            </div>
            <div>
              <dt>设备编号</dt>
              <dd>{item.deviceId}</dd>
            </div>
          </dl>
          <JsonPreview value={item} />
        </details>
      ))}
    </div>
  );
}

function AccessibilitySnapshotList({
  items,
}: {
  items: AccessibilitySnapshotArchive[];
}) {
  if (!items.length) {
    return <p className="inline-empty">未找到与任务时间接近的无障碍快照。</p>;
  }
  return (
    <div className="archive-result-list">
      {items.map((item) => (
        <details key={item.id} className="archive-result-row">
          <summary>
            <span>
              <strong>{item.packageName || "未识别前台应用"}</strong>
              <small>
                {item.windowClass || item.eventType || "无窗口信息"} ·{" "}
                {formatDateTime(item.capturedAt)}
              </small>
            </span>
            <StatusTag
              value={item.status}
              tone={item.status === "idle" ? "gray" : "blue"}
            />
          </summary>
          <dl>
            <div>
              <dt>设备编号</dt>
              <dd>{item.deviceId}</dd>
            </div>
            <div>
              <dt>任务标识</dt>
              <dd>{item.taskId || "未上报"}</dd>
            </div>
            <div>
              <dt>节点摘要</dt>
              <dd>{item.nodeSummary || "无节点摘要"}</dd>
            </div>
          </dl>
          <JsonPreview value={item} />
        </details>
      ))}
    </div>
  );
}

function JsonPreview({
  value,
  empty = "无数据",
}: {
  value: unknown;
  empty?: string;
}) {
  if (value === undefined || value === null) {
    return <p className="json-empty">{empty}</p>;
  }
  return <pre className="json-preview">{JSON.stringify(value, null, 2)}</pre>;
}
